// SEO-12 · /resources/school-timetable-generation-review
//
// EDITORIAL GROUNDING (see docs/marketing/RESOURCES_EDITORIAL_PROCESS.md):
//   • Claims registry D1 (approved wording): generation produces a reviewable DRAFT; conflicts, room
//     collisions and weekly caps surface in a DRY RUN before save; leave opens a substitute board
//     with ranked, HUMAN-ASSIGNED cover; an iCal feed exists. "Constraints produce the draft; a
//     person publishes the timetable." Never say the timetable is auto-published, and never present
//     cover assignment as an automatic swap.
//   • The timetable is also the source of the teacher's record-level scope: `core/teacher_scope`
//     builds allowed sections from `timetable_entries` ∪ `teacher_assignments` ∪ homeroom, so a
//     timetable edit changes who can mark attendance and enter marks (CLAUDE.md, unified 2026-07-08).
//   • Teaching assignments are admin-managed (`/app/teaching-assignments`, timetable:read to view /
//     timetable:update to edit) — a teacher cannot self-grant scope.
// NO scheduling-time claims, no "AI timetable" claim, no optimisation outcome.

const article = {
  slug: "school-timetable-generation-review",
  title: "Timetables: constraints produce a draft, a person publishes it",
  seoTitle: "School Timetable Generation and Review | Vidyapeeth360",
  description:
    "What automated timetabling can and cannot decide for a school, why the dry run matters more than the generator, and what a published timetable controls.",
  topic: "Academics",
  topicKey: "academics",
  readingMinutes: 5,
  published: "2026-08-18",
  updated: "2026-08-18",
  standfirst:
    "A generated timetable is an opinion about a building. It cannot know that the physics teacher leaves at two on Thursdays, or that the hall is unusable during the rains — which is why the review step is the product, not the generator.",
  moduleLinks: ["timetable-management", "teacher-mapping", "student-attendance"],
  conversion: {
    to: "/pricing",
    label: "See plans and the full cost schedule",
    note: "Timetable management is part of the day-one core in every plan — there is no separate scheduling licence.",
  },
  sections: [
    {
      id: "what-a-generator-knows",
      heading: "What a generator knows, and what it does not",
      blocks: [
        {
          t: "p",
          text: "Timetabling looks like a solved problem: subjects, periods, teachers, rooms, constraints, solve. The constraint solving genuinely is the easy part. The hard part is that half of a school's real constraints are not written down anywhere.",
        },
        {
          t: "ul",
          items: [
            "**Encoded and reliable:** teacher availability, subject period counts, room capacity, class-section structure, maximum periods per teacher per day and per week.",
            "**Known but unwritten:** the teacher who takes an early bus, the lab that cannot follow games, the section that should not have double mathematics on Monday, the senior teacher whose free period is by convention.",
            "**Genuinely political:** who gets the good rooms, whose free periods land together, which teacher is not given a first period. A solver optimising a cost function will make these decisions silently and hand you a staffroom problem.",
          ],
        },
        {
          t: "p",
          text: "So the useful output of generation is a draft that satisfies the hard constraints and leaves the rest visibly open — not a finished timetable that has quietly resolved things nobody delegated to it.",
        },
      ],
    },
    {
      id: "dry-run",
      heading: "The dry run is the feature",
      blocks: [
        {
          t: "p",
          text: "Before anything is saved, the school needs to see what would break. Three checks earn their place, and all three should run against the proposed timetable rather than against the saved one.",
        },
        {
          t: "ol",
          items: [
            "**Teacher conflicts** — the same person in two rooms in one period. Obvious, easily created by a manual edit, and the reason most schools discover an error on day one of term.",
            "**Room collisions** — two sections in one lab. Rooms are usually the constraint a school forgot to encode, so it surfaces late.",
            "**Weekly load caps** — a teacher over their agreed periods per week. This one is invisible in a day view and only appears when somebody totals the week, which is exactly what a machine should do.",
          ],
        },
        {
          t: "p",
          text: "Show these as a list somebody can work through, with the ability to accept a specific exception deliberately. A school does run over-cap sometimes; what it should not do is run over-cap by accident.",
        },
      ],
    },
    {
      id: "substitution",
      heading: "Substitution: ranked suggestions, human assignment",
      blocks: [
        {
          t: "p",
          text: "A teacher's leave is approved and four periods need cover. The system knows who is free, who teaches the subject and who has already covered twice this week, and can rank the options in a second.",
        },
        {
          t: "p",
          text: "What it should not do is assign them. Cover is a request to a colleague, made by a person who knows what else is happening that day — and an automatic swap that appears in somebody's timetable overnight is how a school loses trust in its own scheduling system.",
        },
        {
          t: "note",
          title: "Rank, then let a human press the button",
          text: "The right division of labour is the same one that works everywhere else in school software: the machine does the search and the arithmetic; the person makes the decision and owns it. Same-subject cover ranked above free-period cover, recent cover load visible, and one click to assign.",
        },
      ],
    },
    {
      id: "downstream",
      heading: "The timetable is not only a schedule",
      blocks: [
        {
          t: "p",
          text: "Once published, the timetable stops being a plan and starts being a permission structure. It is the most under-appreciated consequence of getting it wrong.",
        },
        {
          t: "p",
          text: "If a teacher's assigned periods determine which sections they can mark attendance for and enter marks against, then an unfinished timetable is also an access problem: a teacher who genuinely teaches a section but is not on its timetable cannot do their job, and a stale entry leaves access with somebody who no longer teaches there. Publishing the timetable and reviewing teaching assignments therefore belong to the same task, at the start of the year.",
        },
      ],
    },
    {
      id: "in-the-product",
      heading: "How this works in Vidyapeeth360",
      blocks: [
        {
          t: "p",
          text: "Generation produces a reviewable draft. The dry run reports teacher conflicts, room collisions and weekly-cap breaches before anything is saved, so the school edits a proposal rather than repairing a published timetable. Nothing publishes itself.",
        },
        {
          t: "p",
          text: "When leave is approved, the affected periods open a substitute board with ranked candidates — subject match, availability and recent cover load — and a person assigns the cover. There is no automatic swap, and the assignment is recorded as somebody's decision.",
        },
        {
          t: "p",
          text: "The published timetable feeds the rest of the year. It is one of the sources that decides a classroom teacher's record-level scope, alongside explicit teaching assignments and the homeroom class a class teacher takes without a timetabled period — which is why teaching assignments are an administrator-managed surface rather than something a teacher can grant themselves. Staff can subscribe to their own schedule as a calendar feed rather than reading a grid.",
        },
        {
          t: "p",
          text: "Practically: build the [timetable](/modules/timetable-management), reconcile it with [teacher mapping](/modules/teacher-mapping), and confirm that the people who will mark [attendance](/modules/student-attendance) in September are the people the timetable says teach those sections. That single check prevents most of the first fortnight's support requests.",
        },
      ],
    },
  ],
  sources: [],
};

export default article;
